import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { BrmActionType } from 'src/common/enumerations/brm-action-type';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import { PriceEntryType } from 'src/common/enumerations/price-entry-type';
import { IItemDetails } from 'src/common/interfaces/dtos/IItemDetails';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { BRMInputActions } from 'src/common/redux/reducers/brm-input-reducer';
import { addItemToTransaction } from 'src/home-page/business-logic/sale-mode-router';
import { saveTransaction } from 'src/home-page/business-logic/save-transaction';
import { lookupItemById } from 'src/items-list/business-logic/item-lookup';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';
import { handlePriceEntryExceptions } from 'src/price-entry/business-logic/handle-price-entry-actions';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { AbortedError } from '../../common/api/extended-emerald-error';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { store } from '../../common/redux/configure-store';
import { InterventionsActions } from '../../common/redux/reducers/interventions-reducer';
import { voidEmptyTransaction } from '../../home-page/business-logic/void-transaction';
import { popupAsyncMessage, popupMessage } from '../../messages/business-logic/popup-message';
import { InterventionTypes } from '../enumerations/intervention-types';
import { IIntervention, IInterventionInfo } from '../interfaces/IIntervention';
import { showInterventionsScreen } from '../interventions-screen';
import { deleteIntervention } from './delete-intervention';
import { getInterventions } from './get-interventions';

export const handleInterventions = async (): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const onClose = () => {
      getCurrentXLRConfig().interventionsSuspendOnCancel
        ? saveTransaction()
        : store.dispatch(InterventionsActions.resetState());
      restorePage();
      reject(new AbortedError());
    };

    const resolveAndClose = () => {
      restorePage();
      resolve();
    };

    const solveIntervention = async (intervention?: IIntervention): Promise<void> => {
      if (intervention) {
        await handleOpenPriceItemIntervention(intervention);
        await deleteIntervention(intervention)
          .then(async () => {
            const interventionInfo = intervention as IInterventionInfo;
            if (interventionInfo.type === InterventionTypes.RestrictedByRule) {
              await getInterventions(false).catch(popupMessage);
            } else {
              const inputData: IItemInputData = {
                itemId: interventionInfo.itemId,
                quantity: 1,
                quantityEntryMethod: EntryMethod.Automatic,
                entryMethod: EntryMethod.Automatic,
              };
              await addItemToTransaction(inputData).catch(async err => {
                await popupAsyncMessage(err);
                await removeItemFromBasketMessage(interventionInfo.itemId);
              });
            }
            closeScreenIfNoIntervetionsLeft();
          })
          .finally(() => {
            store.dispatch(BRMInputActions.resetState());
          });
      }
    };

    const voidItemIntervention = async (intervention?: IIntervention): Promise<void> => {
      if (intervention) {
        await deleteIntervention(intervention)
          .then(async () => {
            const interventionInfo = intervention as IInterventionInfo;
            await removeItemFromBasketMessage(interventionInfo.itemId);
            closeScreenIfNoIntervetionsLeft();
          })
          .catch(popupMessage);
      }
    };

    const closeScreenIfNoIntervetionsLeft = async () => {
      const groups = store.getState().interventions.groups;
      const itemList = store.getState().itemList;
      if (!groups.length) {
        resolveAndClose();
        if (!itemList.itemCount) {
          voidEmptyTransaction();
        }
      }
    };

    showInterventionsScreen({
      onClose,
      resolveAndClose,
      onSolve: solveIntervention,
      onVoidItem: voidItemIntervention,
    });
  });
};

const removeItemFromBasketMessage = (item: string): Promise<void> => {
  const messageModal: IMessageModal = {
    message: 'RecallOrderRemoveFromBasketMsg',
    type: EmeraldDialogTypes.Warning,
    subMessageArgs: { arg: item },
  };
  return popupAsyncMessage(messageModal);
};

const handleOpenPriceItemIntervention = async (intervention: IIntervention) => {
  if (
    intervention.restriction?.additionalData.restrictionCode === BrmActionType.PriceEntryRequired
  ) {
    const itemId =
      intervention.restriction.additionalData.interventionNeededOn.itemRequest.scanData;
    const quantity =
      intervention.restriction.additionalData.interventionNeededOn.itemRequest.quantity.value;
    const uomCode =
      intervention.restriction.additionalData.interventionNeededOn.itemRequest.quantity
        .unitOfMeasure;
    const entryMethod =
      intervention.restriction.additionalData.interventionNeededOn.itemRequest.quantity.entryMethod;

    const itemLookupResult = await lookupItemById({ data: itemId });

    const itemDetails: IItemDetails = {
      price: 0,
      itemId: itemId,
      description: itemLookupResult.description || '',
      quantity: quantity,
    };
    const lineItemData: ILineItemAddData = {
      inputData: {
        itemId: itemId,
        quantity: quantity,
      },
      itemLookup: itemLookupResult,
      lineItem: {
        withData: false,
        item: {
          id: itemId,
          entryMethod: entryMethod,
        },
        quantity: {
          value: quantity,
        },
        uomCode: uomCode,
      },
    };
    await handlePriceEntryExceptions(PriceEntryType.PriceRequired, lineItemData, itemDetails);
  }
};
